import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { label: "Início", href: "#inicio" },
  { label: "Serviços", href: "#servicos" },
  { label: "Sobre", href: "#sobre" },
  { label: "Avaliações", href: "#avaliacoes" },
  { label: "Galeria", href: "#galeria" },
  { label: "Contato", href: "#contato" },
];

const WHATSAPP_URL = "https://wa.me/5562986293474?text=Olá! Visitei o site da RL Refrigeração e gostaria de mais informações.";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-scrolled bg-white" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a href="#inicio" data-testid="navbar-logo" className="flex-shrink-0">
            <img
              src="/assets/RL-Fregrigeracao/logo-semfundo.png"
              alt="RL Refrigeração"
              className="h-14 w-auto"
              loading="eager"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}
                className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#0E86D4] transition-colors uppercase tracking-wider font-['Barlow_Condensed']"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="navbar-cta-whatsapp"
                className="hidden sm:inline-flex bg-[#0E86D4] hover:bg-[#0284C7] text-white font-['Barlow_Condensed'] uppercase tracking-wider text-sm font-bold rounded-sm px-5"
              >
                <Phone className="w-4 h-4 mr-2" />
                Fale Conosco
              </Button>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg mobile-menu"
        >
          <nav className="flex flex-col py-4 px-4 gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className="text-left px-4 py-3 text-base font-semibold text-slate-700 hover:text-[#0E86D4] hover:bg-blue-50 rounded transition-colors font-['Barlow_Condensed'] uppercase tracking-wider"
              >
                {link.label}
              </button>
            ))}
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-2">
              <Button
                data-testid="mobile-cta-whatsapp"
                className="w-full bg-[#0E86D4] hover:bg-[#0284C7] text-white font-['Barlow_Condensed'] uppercase tracking-wider font-bold rounded-sm"
              >
                <Phone className="w-4 h-4 mr-2" />
                Fale Conosco
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
